import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Flame,
  Phone,
  Shield,
  Users,
  Rocket,
  AlertCircle,
  XCircle,
  Brain,
  Target,
  Clock,
  ChevronRight,
  TrendingUp,
  ArrowRight,
  Command,
  X
} from 'lucide-react';
import { HelpSearchEngine } from './searchEngine';
import helpContent from './helpContent';

// Icon mapping
const iconMap = {
  Phone,
  Shield,
  Users,
  Rocket,
  AlertCircle,
  XCircle,
  Flame,
  Brain,
  Target,
  Clock,
  TrendingUp
};

// Category styling
const categoryConfig = {
  'quick-start': {
    label: 'Quick Start',
    icon: Rocket,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200'
  },
  'troubleshooting': {
    label: 'Troubleshooting',
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200'
  },
  'settings': {
    label: 'Settings',
    icon: Target,
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200'
  },
  'features': {
    label: 'Features',
    icon: Brain,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200'
  }
};

const CommandPalette = ({ isOpen, onClose, onNavigate, initialQuery = '' }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedResult, setExpandedResult] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Initialize search engine
  const searchEngine = useMemo(() => new HelpSearchEngine(helpContent), []);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('helpRecentSearches');
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load recent searches:', error);
    }
  }, []);

  // Set initial query when provided
  useEffect(() => {
    if (isOpen && initialQuery) {
      setQuery(initialQuery);
    } else if (!isOpen) {
      setQuery(''); // Reset when closed
    }
  }, [isOpen, initialQuery]);

  // Get search results
  const results = useMemo(() => {
    if (!query.trim()) {
      return searchEngine.getDefaultResults();
    }
    return searchEngine.search(query);
  }, [query, searchEngine]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        if (expandedResult) {
          setExpandedResult(null);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, expandedResult, onClose]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results[selectedIndex]) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, results]);

  const handleResultClick = (result) => {
    // Save to recent searches
    const newRecent = [
      { text: result.question || result.title, id: result.id },
      ...recentSearches.filter(r => r.id !== result.id)
    ].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('helpRecentSearches', JSON.stringify(newRecent));

    // Expand result for inline answer
    setExpandedResult(expandedResult === result.id ? null : result.id);
  };

  const handleQuickAction = (action, result) => {
    if (action.link) {
      onNavigate?.(action.link, action.section);
      onClose();
    }
  };

  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 text-slate-900 font-medium">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const CategoryIcon = ({ category }) => {
    const config = categoryConfig[category] || categoryConfig.settings;
    const Icon = config.icon;
    return <Icon className="w-3.5 h-3.5" />;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        />

        {/* Command Palette */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search help articles, guides, and troubleshooting..."
              className="flex-1 bg-transparent text-slate-900 placeholder-slate-400 outline-none text-base"
            />
            <div className="flex items-center gap-2 flex-shrink-0">
              <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-medium text-slate-500 bg-slate-100 rounded border border-slate-200">
                ⌘K
              </kbd>
              <button
                onClick={onClose}
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {results.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No results found</p>
                <p className="text-sm text-slate-400 mt-1">Try different keywords or check spelling</p>
              </div>
            ) : (
              <div ref={resultsRef} className="py-2">
                {/* Recent Searches (only show when no query and recent exist) */}
                {!query.trim() && recentSearches.length > 0 && (
                  <div className="px-2 mb-3">
                    <div className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Recent Searches
                    </div>
                    <div className="space-y-1">
                      {recentSearches.map((recent) => {
                        const article = helpContent.find(a => a.id === recent.id);
                        if (!article) return null;
                        const config = categoryConfig[article.category] || categoryConfig.settings;
                        return (
                          <button
                            key={recent.id}
                            onClick={() => {
                              const article = helpContent.find(a => a.id === recent.id);
                              if (article) handleResultClick(article);
                            }}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                              <span className="text-sm text-slate-700 flex-1">{recent.text}</span>
                              <span className={`text-xs ${config.color}`}>{config.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Category Header (if results exist) */}
                {!query.trim() && (
                  <div className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5" />
                    {recentSearches.length > 0 ? 'Popular Help Topics' : 'Popular & Recommended'}
                  </div>
                )}

                {results.map((result, index) => {
                  const isSelected = index === selectedIndex;
                  const isExpanded = expandedResult === result.id;
                  const config = categoryConfig[result.category] || categoryConfig.settings;
                  const Icon = iconMap[result.icon] || Target;

                  return (
                    <div key={result.id} className="px-2">
                      {/* Result Item */}
                      <button
                        onClick={() => handleResultClick(result)}
                        className={`w-full text-left px-3 py-3 rounded-xl transition-all ${
                          isSelected
                            ? 'bg-slate-100 shadow-sm'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className={`
                            w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                            ${config.bg} ${config.border} border
                          `}>
                            <Icon className={`w-5 h-5 ${config.color}`} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color} border ${config.border}`}>
                                <CategoryIcon category={result.category} />
                                {config.label}
                              </span>
                            </div>
                            <h3 className="text-sm font-semibold text-slate-900 mb-1">
                              {highlightMatch(result.question || result.title, query)}
                            </h3>
                            <p className="text-sm text-slate-600 line-clamp-2">
                              {result.answerPreview}
                            </p>
                          </div>

                          {/* Expand indicator */}
                          <ChevronRight className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`} />
                        </div>
                      </button>

                      {/* Expanded Answer */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-3 py-4 mt-2 bg-slate-50 rounded-xl border border-slate-200">
                              {/* Full Answer */}
                              <div className="prose prose-sm max-w-none text-slate-700 mb-4 whitespace-pre-line">
                                {result.answer}
                              </div>

                              {/* Steps (if available) */}
                              {result.steps && result.steps.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-semibold text-slate-900 mb-2">
                                    Step-by-Step:
                                  </h4>
                                  <div className="space-y-2">
                                    {result.steps.map((step, idx) => (
                                      <div key={idx} className="flex items-start gap-3">
                                        <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                          {step.step}
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm text-slate-700">{step.text}</p>
                                          {step.action && (
                                            <button
                                              onClick={() => handleQuickAction(step, result)}
                                              className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                            >
                                              {step.action} →
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Quick Action */}
                              {result.quickAction && (
                                <button
                                  onClick={() => handleQuickAction(result.quickAction, result)}
                                  className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                                >
                                  {result.quickAction.label}
                                  <ArrowRight className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">↑↓</kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">↵</kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 font-mono">ESC</kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="hidden sm:block">
              {results.length} {results.length === 1 ? 'result' : 'results'}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CommandPalette;
